import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

/** RFC 1035 maximum lengths for hostnames / domain names. */
export const DOMAIN_NAME_MAX_LENGTH = 253;
const LABEL_MAX_LENGTH = 63;

/**
 * RFC 1035 hostname label: alphanumeric ends, hyphens allowed internally, 1-63 chars.
 */
const HOSTNAME_LABEL_PATTERN =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

export const DOMAIN_NAME_INVALID_MESSAGE = 'Enter a valid domain name.';

export function trimDomainNameInput(raw: unknown): string {
  if (raw == null) {
    return '';
  }
  return String(raw).trim();
}

function containsDisallowedDomainContent(value: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    return true;
  }
  if (value.includes('@')) {
    return true;
  }
  if (/[/?#]/.test(value)) {
    return true;
  }
  if (/:\d+(?:\/|$)/.test(value) || /:\d+$/.test(value)) {
    return true;
  }
  return false;
}

/**
 * Validates a domain name per RFC 1035 hostname label rules.
 * Does not accept protocols, ports, paths, query strings, or email addresses.
 */
export function isValidDomainName(raw: unknown): boolean {
  const value = trimDomainNameInput(raw);
  if (!value || value.length > DOMAIN_NAME_MAX_LENGTH) {
    return false;
  }
  if (containsDisallowedDomainContent(value)) {
    return false;
  }
  if (value.startsWith('.') || value.endsWith('.') || value.includes('..')) {
    return false;
  }

  const labels = value.split('.');
  if (labels.length < 2) {
    return false;
  }

  for (const label of labels) {
    if (!label || label.length > LABEL_MAX_LENGTH) {
      return false;
    }
    if (!HOSTNAME_LABEL_PATTERN.test(label)) {
      return false;
    }
  }

  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/[a-zA-Z]/.test(tld)) {
    return false;
  }

  return true;
}

export function domainNameValidator(
  control: AbstractControl
): ValidationErrors | null {
  const raw = control.value;
  if (raw == null || trimDomainNameInput(raw) === '') {
    return null;
  }

  return isValidDomainName(raw) ? null : { domain: true };
}

export interface DomainNameValidatorOptions {
  required?: boolean;
}

export function getDomainNameValidators(
  options: DomainNameValidatorOptions = {}
): ValidatorFn[] {
  const validators: ValidatorFn[] = [
    Validators.maxLength(DOMAIN_NAME_MAX_LENGTH),
    domainNameValidator,
  ];

  if (options.required) {
    validators.unshift(Validators.required);
  }

  return validators;
}
